/**
 * DataCollectionRecords — "Field Data Management" page.
 *
 * Provides a tab bar (Templates / Promotions) for managing collection
 * templates and asset promotions. Records are now reviewed in the
 * unified Quality Check page.
 */

import { useEffect, useCallback } from "react";
import { Tabs } from "antd";
import {
	FormOutlined,
	RocketOutlined,
	DatabaseOutlined,
	LeftOutlined,
} from "@ant-design/icons";
import { useSearchParams, Link } from "react-router-dom";
import { getTemplates } from "@/services/data-collection-service";
import { useDataCollectionStore } from "@/stores/data-collection-store";
import TemplateBuilder from "./TemplateBuilder";
import AssetPromotions from "./AssetPromotions";
import "./DataCollectionRecords.scss";

const DataCollectionRecords = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const activeTab = searchParams.get("tab") || "templates";

	const { setTemplates } = useDataCollectionStore();

	const fetchTemplates = useCallback(async () => {
		try {
			const t = await getTemplates(true);
			setTemplates(t);
		} catch { /* ignore */ }
	}, []);

	useEffect(() => {
		fetchTemplates();
	}, []);

	const tabItems = [
		{
			key: "templates",
			label: (
				<span className="dc-tab-label">
					<FormOutlined /> Templates
				</span>
			),
			children: <TemplateBuilder />,
		},
		{
			key: "promotions",
			label: (
				<span className="dc-tab-label">
					<RocketOutlined /> Asset Promotions
				</span>
			),
			children: <AssetPromotions />,
		},
	];

	return (
		<div className="dc-records-page">
			<Link to="/forms" className="dc-breadcrumb">
				<LeftOutlined /> Vision Capture
			</Link>

			<div className="dc-page-header">
				<div className="dc-page-title">
					<DatabaseOutlined className="dc-page-icon" />
					<div>
						<h1>Field Data Management</h1>
						<p>Manage collection templates and promote field data into assets.</p>
					</div>
				</div>
			</div>

			<Tabs
				activeKey={activeTab}
				onChange={(key) => setSearchParams({ tab: key })}
				items={tabItems}
				className="dc-tabs"
			/>
		</div>
	);
};

export default DataCollectionRecords;
